/* tslint:disable */
/* eslint-disable */
/**
 * iEduBiofoundry API
 * **Namespacing.** Routes `/api/learn/_*`, tables `learn_*`, Python module `app/learning/`, Python classes prefixed `Learn*`. Within this namespace, \"Session\" means a *learning* Session — a facilitator-created event — never the research workspace in `app/models.py`.  **Three principals, never overlapping:**   * Facilitator — existing oauth2-proxy cookie or `X-API-Key`.   * Learner — `X-Learner-Token`, Visit-scoped, short TTL, heartbeat-renewed. Can write.   * Take-home — capability URL `/learn/me/{token}`. Read-only.  **Game data is opaque.** `LearnAttempt.state` and `LearnArtifact.payload` are arbitrary JSON owned by the frontend. The backend stores and returns them without inspection.  **The two learner tokens are stored differently, on purpose.** The visit token is hashed — it writes, it is short-lived, nobody needs it back. The take-home token is stored recoverably and is only seven characters, because it is read-only and is typed by hand off a printed card; the trade is spelled out in `API_DRAFT.md` §1 and D16.  **Call-by-call frontend walkthrough:** `API_WALKTHROUGH.md` — one Session, several Visits, every exchange in order, including the failure modes.  **Status.** Hand-authored ahead of implementation. Once `app/learning/` exists, FastAPI\'s generated schema becomes the source of truth and this file should be regenerated from it. 
 *
 * The version of the OpenAPI document: 0.1.0-draft
 * Contact: mjberry@illinois.edu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { TakeHomeLink } from './TakeHomeLink';
import {
    TakeHomeLinkFromJSON,
    TakeHomeLinkFromJSONTyped,
    TakeHomeLinkToJSON,
    TakeHomeLinkToJSONTyped,
} from './TakeHomeLink';
import type { LearnArtifactSummary } from './LearnArtifactSummary';
import {
    LearnArtifactSummaryFromJSON,
    LearnArtifactSummaryFromJSONTyped,
    LearnArtifactSummaryToJSON,
    LearnArtifactSummaryToJSONTyped,
} from './LearnArtifactSummary';
import type { ActivityManifest } from './ActivityManifest';
import {
    ActivityManifestFromJSON,
    ActivityManifestFromJSONTyped,
    ActivityManifestToJSON,
    ActivityManifestToJSONTyped,
} from './ActivityManifest';
import type { LearnAttemptSummary } from './LearnAttemptSummary';
import {
    LearnAttemptSummaryFromJSON,
    LearnAttemptSummaryFromJSONTyped,
    LearnAttemptSummaryToJSON,
    LearnAttemptSummaryToJSONTyped,
} from './LearnAttemptSummary';
import type { LearnVisit } from './LearnVisit';
import {
    LearnVisitFromJSON,
    LearnVisitFromJSONTyped,
    LearnVisitToJSON,
    LearnVisitToJSONTyped,
} from './LearnVisit';

/**
 * 
 * @export
 * @interface MyVisit
 */
export interface MyVisit {
    /**
     * 
     */
    visit: LearnVisit;
    /**
     * **The effective manifest for this visit** — the session's when `visit.session_id` is
     * set, and the server-side solo default when it is null. Always present, so the frontend
     * has exactly one place to read the activity list from and no branch on visit kind.
     * 
     * This is what makes a reload survivable. A tab keeps only its visit id and token in
     * `sessionStorage`, so without this field a reloaded tab would hold a valid write token
     * and no way to learn which activities exist: it cannot call
     * `GET /learn/sessions/{sessionId}` (facilitator-only) and it did not keep the join code
     * for `by-code` — which in any case 404s once the session is closed.
     * 
     */
    manifest: ActivityManifest;
    /**
     * The session's display name, for header chrome. **Null for solo play**, which has no
     * session. The id is on `visit.session_id`; this is here so a reloaded tab can render
     * session identity without a facilitator-scoped read.
     * 
     */
    sessionName?: string | null;
    /**
     * Present only once the visit is `ended` or `abandoned`; absent while `active`.
     * 
     * This is the recovery path. `POST /visits/me/end` returns the links too, but a visit
     * ended by `POST /sessions/{sessionId}/close` never calls it — `close` mints links and
     * returns them to nobody. Without this field those learners could never reach their own
     * work. It also covers a reloaded end screen and a cleared tab.
     * 
     */
    takeHomeLinks?: Array<TakeHomeLink>;
    /**
     * 
     */
    attempts: Array<LearnAttemptSummary>;
    /**
     * 
     */
    artifacts: Array<LearnArtifactSummary>;
    /**
     * Advisory, machine-readable notes about this visit. A client should switch on the
     * value and never match prose.
     * 
     * **A separate namespace from `Error.detail`'s slugs, deliberately.** That list is
     * documented as closed — "no route emits one that is not listed" — and folding advisory
     * notes into it would make that claim false the first time one was added.
     * 
     * `single_participant_team`: `team_name` is set but only one participant is named, so
     * `end` will mint one take-home link and a second player leaves with nothing. Emitted
     * here **while the visit is still active**, which is the only time it can be acted on:
     * `PATCH /learn/visits/me` returns `409` once the visit has ended. Triggered on
     * `team_name` rather than on the manifest's `mode: "pair"`, which is advisory to the
     * frontend, is not stored on the attempt, and lives in a session manifest a facilitator
     * can change mid-event — so it would answer a question about the past using mutable
     * present state. The cost of that choice: a pair who never set a team name is not
     * warned.
     * 
     */
    warnings?: Array<MyVisitWarningsEnum>;
}


/**
 * @export
 */
export const MyVisitWarningsEnum = {
    SingleParticipantTeam: 'single_participant_team',
} as const;
export type MyVisitWarningsEnum = typeof MyVisitWarningsEnum[keyof typeof MyVisitWarningsEnum];


/**
 * Check if a given object implements the MyVisit interface.
 */
export function instanceOfMyVisit(value: object): value is MyVisit {
    if (!('visit' in value) || value['visit'] === undefined) return false;
    if (!('manifest' in value) || value['manifest'] === undefined) return false;
    if (!('attempts' in value) || value['attempts'] === undefined) return false;
    if (!('artifacts' in value) || value['artifacts'] === undefined) return false;
    return true;
}

export function MyVisitFromJSON(json: any): MyVisit {
    return MyVisitFromJSONTyped(json, false);
}

export function MyVisitFromJSONTyped(json: any, ignoreDiscriminator: boolean): MyVisit {
    if (json == null) {
        return json;
    }
    return {
        
        'visit': LearnVisitFromJSON(json['visit']),
        'manifest': ActivityManifestFromJSON(json['manifest']),
        'sessionName': json['session_name'] === undefined ? undefined : json['session_name'] === null ? null : json['session_name'],
        'takeHomeLinks': json['take_home_links'] == null ? undefined : ((json['take_home_links'] as Array<any>).map(TakeHomeLinkFromJSON)),
        'attempts': ((json['attempts'] as Array<any>).map(LearnAttemptSummaryFromJSON)),
        'artifacts': ((json['artifacts'] as Array<any>).map(LearnArtifactSummaryFromJSON)),
        'warnings': json['warnings'] == null ? undefined : json['warnings'],
    };
}

export function MyVisitToJSON(json: any): MyVisit {
    return MyVisitToJSONTyped(json, false);
}

export function MyVisitToJSONTyped(value?: MyVisit | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'visit': LearnVisitToJSON(value['visit']),
        'manifest': ActivityManifestToJSON(value['manifest']),
        'session_name': value['sessionName'],
        'take_home_links': value['takeHomeLinks'] == null ? undefined : ((value['takeHomeLinks'] as Array<any>).map(TakeHomeLinkToJSON)),
        'attempts': ((value['attempts'] as Array<any>).map(LearnAttemptSummaryToJSON)),
        'artifacts': ((value['artifacts'] as Array<any>).map(LearnArtifactSummaryToJSON)),
        'warnings': value['warnings'],
    };
}

